import React, { useState } from 'react';
import './DataTable.css';

// ─── Valeurs prédéfinies (miroir de ValidationModal) ──────────────────────────
const OPTIONS = {
  typeDocument:      ['Contrat', 'Quittance'],
  quittancement:     ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Mensuelle', 'Unique', 'Biennale', 'Triennale', 'Décennale'],
  typeToiture:       ['Traditionnelle', 'Terrasse', 'Métallique', 'Végétale', 'Mixte'],
  usageBatiment:     ['Ensemble vertical', 'Ensemble horizontal', 'Habitation & bureau', 'Habitation & commerce', '100% bureau', '100% commerce', 'Garage'],
  commerce:          ['Pas de commerce', '< 25%', '25% < surfaces commerces < 50%', '> 50%', '100%'],
  zoneInondable:     ['Oui', 'Non'],
  quartierPrioritaire: ['Oui', 'Non'],
  altitude:          ['<1500', '>1500'],
  natureChauffage:   ['Individuel', 'Collectif'],
  mh:                ['Oui', 'Non'],
  tauxVide:          ['<= 25%', '<= 50%', '<= 75%', '> 75%', '100%'],
};

const MULTI_OPTIONS = {
  typeChauffage: [
    'Electrique', 'Gaz', 'Pompe à chaleur - Climatisation réversible ou non',
    'Poêle ou cheminée à Bois ou Granule', 'Photovoltaique', 'Fioul', 'Aucun', 'Autre'
  ],
  installationEquipement: [
    'Piscine exterieure', 'Piscine Interieure', 'Tennis / Padel / Squash',
    'Résidence de service', 'Résidence sénior', 'Résidence étudiantes',
    'SPA / jacuzzi / Sauna / hammam / salle de fitness', 'Résidence saisonnière',
    'Panneau Photovoltaique', 'Panneau Solaire'
  ],
  BrisMachine: [
    'Portail électrique', 'Ascenseur / monte charge', 'Pompe de relevage', 'Autre'
  ],
};

// ─── Composants module-level (référence stable → pas de perte de focus) ────────

function EditableField({ fieldKey, label, value, onChange }) {
  return (
    <div className="editable-field">
      <label>{label}</label>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
      />
    </div>
  );
}

function SelectField({ fieldKey, label, value, onChange, options }) {
  return (
    <div className="editable-field">
      <label>{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        className="dt-select"
      >
        <option value="">— choisir —</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function CheckGroup({ fieldKey, label, options, value, onChange }) {
  const values = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    const next = values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt];
    onChange(fieldKey, next);
  };
  return (
    <div className="editable-field dt-check-group">
      <label>{label}</label>
      <div className="dt-check-options">
        {options.map(opt => (
          <label key={opt} className="dt-check-option">
            <input type="checkbox" checked={values.includes(opt)} onChange={() => toggle(opt)} />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Raccourcis
function F({ k, l, data, onChange }) {
  return <EditableField fieldKey={k} label={l} value={data[k]} onChange={onChange} />;
}
function SF({ k, l, data, onChange }) {
  return <SelectField fieldKey={k} label={l} value={data[k]} onChange={onChange} options={OPTIONS[k]} />;
}
function CG({ k, l, data, onChange }) {
  return <CheckGroup fieldKey={k} label={l} options={MULTI_OPTIONS[k]} value={data[k]} onChange={onChange} />;
}

// ─── DataTable ────────────────────────────────────────────────────────────────

function DataTable({ data, onDeleteDocument, onUpdateDocument }) {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);

  // Colonnes principales à afficher dans le tableau
  const mainColumns = [
    { key: 'typeDocument', label: 'Type' },
    { key: 'nomCopropriete', label: 'Copropriété' },
    { key: 'assureur', label: 'Assureur' },
    { key: 'numeroContrat', label: 'N° Contrat' },
    { key: 'primeTTC', label: 'Prime TTC', format: 'currency' },
    { key: 'primeAnnuelle', label: 'Prime Annuelle', format: 'currency' },
    { key: 'evolutionPrime', label: 'Évolution', format: 'evolution' },
    { key: 'periodeDebut', label: 'Début' },
    { key: 'periodeFin', label: 'Fin' },
    { key: 'metadata.filename', label: 'Fichier' }
  ];

  const handleEdit = (doc) => {
    setEditingId(doc.id);
    setEditData({ ...doc });
  };

  const handleSave = () => {
    onUpdateDocument(editingId, editData);
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleDelete = (docId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      onDeleteDocument(docId);
    }
  };

  const handleInputChange = (key, value) => {
    setEditData(prev => ({ ...prev, [key]: value }));
  };

  const toggleExpand = (docId) => {
    setExpandedRow(expandedRow === docId ? null : docId);
  };

  // Formatage des valeurs
  const formatValue = (value, format) => {
    if (value === null || value === undefined) return '-';

    switch (format) {
      case 'currency':
        return typeof value === 'number' ? `${value.toLocaleString('fr-FR')} €` : value;
      case 'evolution':
        if (value === null || value === undefined) return '-';
        const sign = value > 0 ? '+' : '';
        const color = value > 0 ? 'evolution-up' : value < 0 ? 'evolution-down' : 'evolution-stable';
        return <span className={`evolution-badge ${color}`}>{sign}{value}%</span>;
      default:
        return value;
    }
  };

  return (
    <div className="data-table-container">
      <div className="table-header">
        <h2>Données extraites ({data.length} documents)</h2>
        <div className="model-badge">
          <span className="model-icon">🤖</span>
          Claude Vision
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th className="th-expand"></th>
              {mainColumns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th className="th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <React.Fragment key={item.id}>
                <tr className={`${expandedRow === item.id ? 'expanded' : ''} dt-tr-clickable`} onClick={() => handleEdit(item)} title="Cliquer pour modifier">
                  <td className="td-expand">
                    <button
                      className="expand-button"
                      onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
                      title="Voir les détails"
                    >
                      {expandedRow === item.id ? '▼' : '▶'}
                    </button>
                  </td>
                  <td>
                    <span className={`type-badge ${item.typeDocument}`}>
                      {item.typeDocument === 'contrat' ? '📄' :
                       item.typeDocument === 'quittance' ? '🧾' :
                       item.typeDocument === 'quittance_historique' ? '📜' :
                       item.typeDocument === 'contrat_avec_quittance' ? '📋' :
                       item.typeDocument === 'appel_prime' ? '📨' :
                       item.typeDocument === 'avenant' ? '📝' : '📋'}
                      {item.typeDocument === 'contrat_avec_quittance' ? 'contrat+quit' :
                       item.typeDocument === 'quittance_historique' ? 'historique' :
                       item.typeDocument || 'N/A'}
                    </span>
                  </td>
                  <td>{item.nomCopropriete || item.adresseImmeuble || '-'}</td>
                  <td>{item.assureur || '-'}</td>
                  <td className="code-cell">{item.numeroContrat || '-'}</td>
                  <td className="amount-cell">
                    {formatValue(item.primeTTC, 'currency')}
                  </td>
                  <td className="amount-cell">
                    {formatValue(item.primeAnnuelle || item.primeAnnuelleCalculee, 'currency')}
                  </td>
                  <td>{formatValue(item.evolutionPrime, 'evolution')}</td>
                  <td>{item.periodeDebut || item.dateDebut || '-'}</td>
                  <td>{item.periodeFin || item.dateFin || '-'}</td>
                  <td className="file-cell" title={item.metadata?.filename}>
                    {item.metadata?.filename?.substring(0, 20) || '-'}
                    {item.metadata?.filename?.length > 20 && '...'}
                  </td>
                  <td className="td-actions">
                    <button
                      className="action-button delete"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>

                {/* Ligne de détails expansée */}
                {expandedRow === item.id && (
                  <tr className="detail-row">
                    <td colSpan={mainColumns.length + 2}>
                      <div className="detail-content">
                        <div className="detail-section">
                          <h4>📍 Syndic</h4>
                          <div className="detail-grid">
                            <div><strong>Nom:</strong> {item.nomSyndic || '-'}</div>
                            <div><strong>Adresse:</strong> {item.adresseSyndic || '-'}</div>
                            <div><strong>CP:</strong> {item.cpSyndic || '-'}</div>
                            <div><strong>Ville:</strong> {item.villeSyndic || '-'}</div>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4>🏢 Copropriété / Immeuble</h4>
                          <div className="detail-grid">
                            <div><strong>Nom:</strong> {item.nomCopropriete || '-'}</div>
                            <div><strong>Adresse:</strong> {item.adresseImmeuble || '-'}</div>
                            <div><strong>CP:</strong> {item.cpImmeuble || '-'}</div>
                            <div><strong>Ville:</strong> {item.villeImmeuble || '-'}</div>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4>👤 Agent / Courtier</h4>
                          <div className="detail-grid">
                            <div><strong>Nom:</strong> {item.nomAgent || '-'}</div>
                            <div><strong>Tél:</strong> {item.telAgent || '-'}</div>
                            <div><strong>Email:</strong> {item.emailAgent || '-'}</div>
                            <div><strong>Adresse:</strong> {item.adresseAgent || '-'}</div>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4>📋 Contrat & Quittance</h4>
                          <div className="detail-grid">
                            <div><strong>Période:</strong> {item.periodeDebut || '-'} → {item.periodeFin || '-'}</div>
                            <div><strong>Prime TTC:</strong> {item.primeTTC ? `${item.primeTTC} €` : '-'}</div>
                            <div><strong>Prime annuelle:</strong> {(item.primeAnnuelle || item.primeAnnuelleCalculee) ? `${item.primeAnnuelle || item.primeAnnuelleCalculee} €` : '-'}</div>
                            <div><strong>Évolution:</strong> {item.evolutionPrime !== undefined && item.evolutionPrime !== null ? `${item.evolutionPrime > 0 ? '+' : ''}${item.evolutionPrime}%` : '-'}</div>
                            <div><strong>Échéance:</strong> {item.echeanceAnnuelle || '-'}</div>
                            <div><strong>Indice FFB:</strong> {item.indiceFFB || '-'}</div>
                            <div><strong>Indice FFB Contrat:</strong> {item.indiceFFBContrat || '-'}</div>
                            <div><strong>Quittancement:</strong> {item.quittancement || '-'}</div>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4>🏗️ Bâtiment</h4>
                          <div className="detail-grid">
                            <div><strong>Nb bâtiments:</strong> {item.nbBatiments || '-'}</div>
                            <div><strong>Surface:</strong> {item.surfaceTotale ? `${item.surfaceTotale} m²` : '-'}</div>
                            <div><strong>Niveaux:</strong> {item.nbNiveaux || '-'}</div>
                            <div><strong>Sous-sols:</strong> {item.nbSousSol ?? '-'}</div>
                            <div><strong>Année construction:</strong> {item.anneeConstruction || '-'}</div>
                            <div><strong>Usage:</strong> {item.usageBatiment || '-'}</div>
                            <div><strong>Toiture:</strong> {item.typeToiture || '-'}</div>
                            <div><strong>Commerce:</strong> {item.commerce || '-'}</div>
                            <div><strong>Zone inondable:</strong> {item.zoneInondable || '-'}</div>
                            <div><strong>Quartier prioritaire:</strong> {item.quartierPrioritaire || '-'}</div>
                            <div><strong>Altitude:</strong> {item.altitude || '-'}</div>
                            <div><strong>% Mat. durs:</strong> {item.pourcentageMatDurs || '-'}</div>
                            <div><strong>% Vide d'occupant:</strong> {item.tauxVide || '-'}</div>
                            <div><strong>Nature chauffage:</strong> {item.natureChauffage || '-'}</div>
                            <div><strong>MH:</strong> {item.mh || '-'}</div>
                            <div><strong>Type chauffage:</strong> {Array.isArray(item.typeChauffage) && item.typeChauffage.length > 0 ? item.typeChauffage.join(', ') : '-'}</div>
                            <div><strong>Équipements:</strong> {Array.isArray(item.installationEquipement) && item.installationEquipement.length > 0 ? item.installationEquipement.join(', ') : '-'}</div>
                            <div><strong>Bris de machine:</strong> {Array.isArray(item.BrisMachine) && item.BrisMachine.length > 0 ? item.BrisMachine.join(', ') : '-'}</div>
                            {item.travaux10Ans && <div className="full-width"><strong>Travaux 10 ans:</strong> {item.travaux10Ans}</div>}
                          </div>
                        </div>

                        <div className="detail-section full-width">
                          <h4>🛡️ Garanties</h4>
                          <p>{item.garanties || '-'}</p>
                        </div>

                        <div className="detail-section full-width">
                          <h4>💰 Franchises</h4>
                          <div className="detail-grid">
                            <div><strong>Générale:</strong> {item.franchiseGenerale ? `${item.franchiseGenerale} €` : '-'}</div>
                            <div><strong>Spécifiques:</strong> {item.franchisesSpecifiques || '-'}</div>
                            <div><strong>Limite Contractuelle d'Indemnité (LCI en €):</strong> {item.lci || '-'}</div>
                          </div>
                        </div>

                        {item.exclusions && (
                          <div className="detail-section full-width">
                            <h4>⛔ Exclusions</h4>
                            <p>{item.exclusions}</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal d'édition */}
      {editingId && (
        <div className="edit-modal-overlay" onClick={handleCancel}>
          <div className="edit-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>Modifier le document</h3>
              <button className="close-button" onClick={handleCancel}>×</button>
            </div>
            <div className="edit-modal-content">

              <div className="edit-section">
                <h4>Informations générales</h4>
                <div className="edit-grid">
                  <SF k="typeDocument" l="Type Document" data={editData} onChange={handleInputChange} />
                  <F  k="assureur" l="Assureur" data={editData} onChange={handleInputChange} />
                  <F  k="numeroContrat" l="N° Contrat" data={editData} onChange={handleInputChange} />
                </div>
              </div>

              <div className="edit-section">
                <h4>Syndic</h4>
                <div className="edit-grid">
                  <F k="nomSyndic" l="Nom" data={editData} onChange={handleInputChange} />
                  <F k="adresseSyndic" l="Adresse" data={editData} onChange={handleInputChange} />
                  <F k="cpSyndic" l="Code Postal" data={editData} onChange={handleInputChange} />
                  <F k="villeSyndic" l="Ville" data={editData} onChange={handleInputChange} />
                </div>
              </div>

              <div className="edit-section">
                <h4>Copropriété</h4>
                <div className="edit-grid">
                  <F k="nomCopropriete" l="Nom" data={editData} onChange={handleInputChange} />
                  <F k="adresseImmeuble" l="Adresse" data={editData} onChange={handleInputChange} />
                  <F k="cpImmeuble" l="Code Postal" data={editData} onChange={handleInputChange} />
                  <F k="villeImmeuble" l="Ville" data={editData} onChange={handleInputChange} />
                </div>
              </div>

              <div className="edit-section">
                <h4>Agent / Courtier</h4>
                <div className="edit-grid">
                  <F k="nomAgent" l="Nom" data={editData} onChange={handleInputChange} />
                  <F k="adresseAgent" l="Adresse" data={editData} onChange={handleInputChange} />
                  <F k="cpAgent" l="Code Postal" data={editData} onChange={handleInputChange} />
                  <F k="villeAgent" l="Ville" data={editData} onChange={handleInputChange} />
                  <F k="telAgent" l="Téléphone" data={editData} onChange={handleInputChange} />
                  <F k="emailAgent" l="Email" data={editData} onChange={handleInputChange} />
                </div>
              </div>

              <div className="edit-section">
                <h4>Contrat & Quittance</h4>
                <div className="edit-grid">
                  <F  k="primeTTC" l="Prime TTC (€)" data={editData} onChange={handleInputChange} />
                  <F  k="primeAnnuelle" l="Prime Annuelle (€)" data={editData} onChange={handleInputChange} />
                  <F  k="periodeDebut" l="Date Début" data={editData} onChange={handleInputChange} />
                  <F  k="periodeFin" l="Date Fin" data={editData} onChange={handleInputChange} />
                  <F  k="evolutionPrime" l="Évolution (%)" data={editData} onChange={handleInputChange} />
                  <F  k="echeanceAnnuelle" l="Échéance Annuelle" data={editData} onChange={handleInputChange} />
                  <F  k="indiceFFB" l="Indice FFB" data={editData} onChange={handleInputChange} />
                  <F  k="indiceFFBContrat" l="Indice FFB Contrat" data={editData} onChange={handleInputChange} />
                  <SF k="quittancement" l="Quittancement" data={editData} onChange={handleInputChange} />
                </div>
              </div>

              <div className="edit-section">
                <h4>Immeuble</h4>
                <div className="edit-grid">
                  <F  k="nbBatiments" l="Nb Bâtiments" data={editData} onChange={handleInputChange} />
                  <F  k="surfaceTotale" l="Surface (m²)" data={editData} onChange={handleInputChange} />
                  <F  k="nbNiveaux" l="Nb Niveaux" data={editData} onChange={handleInputChange} />
                  <F  k="nbSousSol" l="Nb Sous-sols" data={editData} onChange={handleInputChange} />
                  <F  k="anneeConstruction" l="Année construction" data={editData} onChange={handleInputChange} />
                  <SF k="usageBatiment" l="Usage" data={editData} onChange={handleInputChange} />
                  <SF k="typeToiture" l="Type Toiture" data={editData} onChange={handleInputChange} />
                  <SF k="commerce" l="Commerce" data={editData} onChange={handleInputChange} />
                  <F  k="typeCommerce" l="Type Commerce" data={editData} onChange={handleInputChange} />
                  <SF k="zoneInondable" l="Zone Inondable" data={editData} onChange={handleInputChange} />
                  <SF k="quartierPrioritaire" l="Quartier Prioritaire" data={editData} onChange={handleInputChange} />
                  <SF k="altitude" l="Altitude" data={editData} onChange={handleInputChange} />
                  <F  k="pourcentageMatDurs" l="% Matériaux durs" data={editData} onChange={handleInputChange} />
                  <SF k="tauxVide" l="% Vide d'occupant" data={editData} onChange={handleInputChange} />
                  <SF k="natureChauffage" l="Nature chauffage" data={editData} onChange={handleInputChange} />
                  <SF k="mh" l="Monument Historique" data={editData} onChange={handleInputChange} />
                  <F  k="travaux10Ans" l="Travaux (10 ans)" data={editData} onChange={handleInputChange} />
                </div>
                <div className="edit-grid dt-grid-full">
                  <CG k="typeChauffage" l="Type de chauffage" data={editData} onChange={handleInputChange} />
                  <CG k="installationEquipement" l="Installations / Équipements" data={editData} onChange={handleInputChange} />
                  <CG k="BrisMachine" l="Bris de machine" data={editData} onChange={handleInputChange} />
                </div>
              </div>

              <div className="edit-section">
                <h4>Garanties et Franchises</h4>
                <div className="edit-grid full-width">
                  <F k="garanties" l="Garanties" data={editData} onChange={handleInputChange} />
                  <F k="exclusions" l="Exclusions" data={editData} onChange={handleInputChange} />
                  <F k="franchiseGenerale" l="Franchise Générale (€)" data={editData} onChange={handleInputChange} />
                  <F k="franchisesSpecifiques" l="Franchises Spécifiques" data={editData} onChange={handleInputChange} />
                  <F k="lci" l="Limite Contractuelle d'Indemnité (LCI en €):" data={editData} onChange={handleInputChange} />
                </div>
              </div>

            </div>
            <div className="edit-modal-footer">
              <button className="cancel-button" onClick={handleCancel}>Annuler</button>
              <button className="save-button" onClick={handleSave}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
